/* eslint semi:0 */
import m from 'mithril'
import BaseView from './BaseView'
import ButtonView from './ButtonView'
import { objectMap } from '../../utils/object'
import { t, locale } from '../../providers/i18nProvider/i18nProvider'
import { hiddenInput, requiredInput, textarea, select, optionTag, checkbox } from './InputView'
import { domainFromFqdn } from '../../utils/ua'

const requiredCheckbox = checkbox.part({ isRequired: true })
const SITES_WITH_GAMES = ['dbltap']
const SITES_WITHOUT_TEAMS = SITES_WITH_GAMES

function fieldset(children) {
  return m('fieldset', children)
}

function formItem(children, attrs = {}) {
  return m('.form-item', attrs, children)
}

export default class SignupModalView extends BaseView {
  view(ctrl) {
    let { vm } = ctrl
    return m(
      '.signup-modal',
      {
        class: vm.isLoading() ? '.signup-modal--loading' : '',
      },
      [
        m('header.modal__header', m('h1.modal__title', t('sign_up.lets_start'))),
        m('section.modal__body', [
          // m('p.centered', t('sign_up.change')),
          this.serverErrorsList(vm),
          this.formView(vm),
        ]),
      ]
    )
  }

  getFormViewAttrs(vm) {
    let { submit, onFieldChange } = vm
    ;({ submit, onFieldChange } = objectMap({ submit, onFieldChange }, function bindHandler(
      handlerName,
      handlerFn
    ) {
      return { [handlerName]: handlerFn.bind(vm) }
    }))
    return {
      config: function bindFormValidityToVm(el, isInitialized) {
        if (!isInitialized) {
          vm.formValidityGetter = el.checkValidity.bind(el)
        }
      },
      onchange: onFieldChange,
      oninput: onFieldChange,
      // FIXME: figure out how to make focus/onfocusin/out/blur work, then remove 'click'
      onfocusin: onFieldChange,
      onfocusout: onFieldChange,
      onclick: onFieldChange,
      onsubmit: submit,
    }
  }

  formView(vm) {
    let { store } = vm
    let joinKey = 'sign_up.join_' + domainFromFqdn()
    return m('form[name=signup-new-user][novalidate]', this.getFormViewAttrs(vm), [
      fieldset([
        hiddenInput({
          value: locale,
          name: 'user[locale]',
        }),
        hiddenInput({
          name: 'auth_id',
          value: `${store.authId}`,
        }),
        formItem(
          m('label', [
            m('span.form-item__title', t('sign_up.disp_name')),
            requiredInput({
              // TODO: don't spellcheck - fix this in InputView
              spellcheck: false,
              // TODO: fix autofocus
              autofocus: true,
              name: 'user[nick]',
              classList: ['username'],
              minlength: 5,
              maxlength: 50,
              pattern: '[^~!@#$%^&*\\?\\(\\)\\+=\\{\\}\\[\\],<>~`"\\|:;\\\\]{5,50}',
              misvalidationMessage: m('.form-item__error', t('errors.nick.validation')),
            }),
          ])
        ),
        formItem(
          m('label', [
            m('span.form-item__title', t('sign_up.about_you')),
            textarea({
              classList: ['signup-modal__motto'],
              name: 'user[motto]',
              maxlength: 250,
            }),
          ])
        ),
        this.leagueSelectView(vm),
        this.teamSelectView(vm),
        this.languageSelectView(vm),
      ]),
      fieldset(
        m(
          '.form-item',
          m(
            'label.checkbox.signup-modal__terms',
            requiredCheckbox({
              siblings: [m('span', this.agreeToTermsAndConditionsView(vm))],
              misvalidationMessage: m('.form-item__error', t('sign_up.required_field')),
            })
          )
        )
      ),
      ButtonView.make({
        type: 'submit',
        children: t(joinKey),
        isDisabled: !vm.isFormValid() || vm.isLoading(),
      }),
    ])
  }

  agreeToTermsAndConditionsView(vm) {
    let signUpKey = 'sign_up.of_' + domainFromFqdn()
    return [
      t('sign_up.terms'),
      ButtonView.make({
        isNewTab: true,
        href: `${vm.termsUrl}`,
        children: t('sign_up.terms_and_conditions'),
      }),
      ' ',
      t('sign_up.and'),
      ' ',
      ButtonView.make({
        isNewTab: true,
        href: `${vm.privacyUrl}`,
        children: t('sign_up.privacy_policy'),
      }),
      ' ',
      t(signUpKey),
    ]
  }

  leagueSelectView(vm) {
    const label = SITES_WITH_GAMES.includes(domainFromFqdn())
      ? t('sign_up.my_game')
      : t('sign_up.my_league')
    let { leagues, localeLeagueName } = vm
    let selectOptions = leagues.map(function getLeagueOptionTag(league) {
      let { name: leagueName, key: leagueId } = league
      return optionTag(leagueName, {
        value: leagueId,
        isSelected: leagueName === localeLeagueName,
      })
    })
    return formItem(
      m(
        'label',
        m('span.form-item__title', label),
        m(
          '.form-select',
          select({
            onchange: m.withAttr('value', vm.selectedLeague),
            classList: ['dropdown', 'league'],
            name: 'user[league_slug]',
            children: selectOptions,
          })
        )
      )
    )
  }

  teamSelectView(vm) {
    if (SITES_WITHOUT_TEAMS.includes(domainFromFqdn())) {
      return null
    }
    let teams = vm.teams[vm.selectedLeague()] || []
    let selectOptions = teams.map(function getTeamOptionTag(team) {
      return optionTag(team.name, { value: team.id })
    })
    return formItem(
      m(
        'label',
        m('span.form-item__title', t('sign_up.team')),
        m(
          '.form-select',
          select({
            classList: ['dropdown', 'team'],
            name: 'user[subject_id]',
            children: selectOptions,
          })
        )
      )
    )
  }

  languageSelectView(vm) {
    let selectOptions = vm.languages.map(function addLanguageOptionTag(language) {
      return optionTag(t(`common.languages.${language}`), {
        value: language,
        isSelected: language === locale,
      })
    })
    return formItem(
      m(
        'label',
        m('span.form-item__title', t('common.language')),
        m(
          '.form-select',
          select({
            name: 'user[writing_locale]',
            children: selectOptions,
          })
        )
      )
    )
  }

  serverErrorsList(vm) {
    let errors = vm.serverValidationErrors()
    let errorsListItems = []

    for (let key of Object.keys(errors)) {
      let itemErrors = errors[key]
      let listItem = this.serverErrorListItem(key, itemErrors)
      errorsListItems.push(listItem)
    }

    return m('ul.server-side-errors-wrap', errorsListItems)
  }

  serverErrorListItem(key, errors) {
    let subItems = []
    for (let error of errors) {
      let subItem = this.serverErrorSublistItem(error)
      subItems.push(subItem)
    }
    return m('li.server-side-errors-field', [m('ul.server-side-error-field-values', subItems)])
  }

  serverErrorSublistItem(error) {
    return m('li.server-side-error-field-value', error)
  }
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/views/SignupModalView.js